import {useParams, useSearchParams} from "react-router";
import GarrisonTimelineControlPanel from "./components/timeline/components/garrison-timeline-control-panel.jsx";
import GarrisonTimelineSlider from "./components/timeline/components/garrison-timeline-slider.jsx";
import {Template} from "../../../components/template/template.jsx";
import {Card} from "../../../components/card/card.jsx";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {format, isValid, parse} from "date-fns";
import corporationService from "../../../services/web/corporation-service.js";
import {garrisonTimelineKeys, garrisonTimelineModes} from "./utils/garrison-timeline-utils.js";
import {useTimelineAnimation} from "./hooks/useTimelineAnimation.js";
import GarrisonRouteMap from "./components/route/map/garrison-route-map.jsx";
import garrisonTimelineService from "../../../services/dispatch/garrison-timeline-service.js";
import PrintTemplate from "../../../components/reports/print-reports/print-template.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import LegendDropdown from "../../../components/legend-dropdown/legend-dropdown.jsx";
import ReportUtils from "../../../utils/report-utils.js";



const parseFilterDate = (value) => {
  if (!value) return new Date();

  const parsedDateWithSeconds = parse(value, "dd/MM/yyyy HH:mm:ss", new Date());
  if (isValid(parsedDateWithSeconds)) return parsedDateWithSeconds;

  const parsedDateWithoutSeconds = parse(value, "dd/MM/yyyy HH:mm", new Date());
  if (isValid(parsedDateWithoutSeconds)) return parsedDateWithoutSeconds;

  const fallbackDate = new Date(value);
  return isValid(fallbackDate) ? fallbackDate : new Date();
};

const ROUTE_MAP_LEGEND_ITEMS = [
  {label: "Em despacho / Em atividade", lineClassName: "h-0 w-7 border-t-2 border-dashed border-[#0082FF]"},
  {label: "Disponível", lineClassName: "h-0 w-7 border-t-2 border-[#0082FF]"},
  {label: "Posição registrada por GPS — sem trajeto confirmado", lineClassName: "h-3 w-3 rounded-full border-2 border-white bg-[#0082FF] shadow"}
];

const GarrisonRouteView = () => {
  const {id} = useParams();
  const [searchParams] = useSearchParams();
  const dtStart = searchParams.get("dtStart");
  const dtEnd = searchParams.get("dtEnd");
  const orderIndex = Number(searchParams.get("orderIndex"));
  const hasValidRouteFilters = [id, dtStart, dtEnd].every(value => typeof value === "string" && value.trim());
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const animationControl = useTimelineAnimation(elapsedSeconds, setElapsedSeconds);
  const printComponentRef = useRef(null);
  const [headerSelected, setHeaderSelected] = useState(-1);
  const [isPrinting, setIsPrinting] = useState(false);
  const filter = useMemo(() => ({dtStart, dtEnd}), [dtStart, dtEnd]);
  const routeZoom = useMemo(() => ({start: 0, end: 100}), []);
  const {data: routeData, isLoading: isLoadingRoute} = useQuery({
    queryKey: garrisonTimelineKeys.route(id, dtStart, dtEnd, orderIndex),
    queryFn: () => garrisonTimelineService.routes({
      dtStart,
      dtEnd,
      garrisonIds: [id],
      orderIndexes: Number.isInteger(orderIndex) && orderIndex > 0 ? {[id]: orderIndex} : {}
    }),
    enabled: hasValidRouteFilters
  });

  const garrisonsList = routeData?.garrisons || [];
  const timelineRoutes = routeData?.routes || {};
  const firstGarrison = garrisonsList[0];

  const effectiveFilter = useMemo(() => {
    if (!timelineRoutes || Object.keys(timelineRoutes).length === 0) return filter;

    let minTime = null;
    let maxTime = null;

    Object.values(timelineRoutes).forEach(points => {
      if (!Array.isArray(points)) return;

      points.forEach(point => {
        if (!point.time) return;
        const pointTime = parse(point.time, "dd/MM/yyyy HH:mm:ss", new Date()).getTime();

        if (minTime === null || pointTime < minTime) minTime = pointTime;
        if (maxTime === null || pointTime > maxTime) maxTime = pointTime;
      });
    });

    if (minTime !== null && maxTime !== null) {
      return {
        ...filter,
        dtStart: format(new Date(minTime), "dd/MM/yyyy HH:mm:ss"),
        dtEnd: format(new Date(maxTime), "dd/MM/yyyy HH:mm:ss")
      };
    }

    return filter;
  }, [timelineRoutes, filter]);

  const initialSyncDone = useRef(false);

  const routeName = routeData?.garrisons[0]
    ? `- ${routeData?.garrisons[0]?.orderIndex} - ${routeData?.garrisons[0]?.name}`
    : ""

  document.title = `Rota da guarnição ${routeName ? routeName : ""}`;

  useEffect(() => {
    if (routeData && !initialSyncDone.current) {
      let earliestTimeInSeconds = null;

      const dtStartStr = effectiveFilter.dtStart || new Date().toISOString();
      const baseStartObj = parseFilterDate(dtStartStr);
      const baseStart = baseStartObj.getTime();

      Object.values(timelineRoutes).forEach(points => {
        if (points && points.length > 0) {
          const firstPoint = points[0];
          const pointTime = parse(firstPoint.time, "dd/MM/yyyy HH:mm:ss", new Date()).getTime();
          const timeInSeconds = (pointTime - baseStart) / 1000;

          if (earliestTimeInSeconds === null || timeInSeconds < earliestTimeInSeconds) {
            earliestTimeInSeconds = timeInSeconds;
          }
        }
      });

      if (earliestTimeInSeconds !== null) {
        const visibleStartSeconds = Math.max(0, earliestTimeInSeconds);
        const endDate = parseFilterDate(effectiveFilter.dtEnd);
        const visibleTotalSeconds = Math.max(0, (endDate.getTime() - baseStart) / 1000);
        const maxCropSize = Math.min(14400, visibleTotalSeconds);

        setElapsedSeconds(visibleStartSeconds);

        if (animationControl && animationControl.setCropRange) {
          animationControl.setCropRange([visibleStartSeconds, visibleStartSeconds + maxCropSize]);
        }

        initialSyncDone.current = true;
      }
    }
  }, [timelineRoutes, effectiveFilter, setElapsedSeconds, animationControl]);

  useEffect(() => {
    if (isPrinting) {
      const getAbsoluteDate = (seconds) => {
        const dtStart = effectiveFilter?.dtStart || new Date().toISOString();
        const baseStartObj = parseFilterDate(dtStart);
        return new Date(baseStartObj.getTime() + seconds * 1000);
      };

      const printFilters = {
        "Data/hora inicial": effectiveFilter?.dtStart,
        "Data/hora final": effectiveFilter?.dtEnd
      };

      if (animationControl.isCropMode && animationControl.cropRange) {
        const cropStart = getAbsoluteDate(animationControl.cropRange[0]);
        const cropEnd = getAbsoluteDate(animationControl.cropRange[1]);
        printFilters["Período Analisado"] = `${format(cropStart, "dd/MM/yyyy HH:mm:ss")} à ${format(cropEnd, "dd/MM/yyyy HH:mm:ss")}`;
      }

      printFilters["Momento Atual no Mapa"] = format(getAbsoluteDate(elapsedSeconds), "dd/MM/yyyy HH:mm:ss");

      ReportUtils.appendFilters(printFilters);
    }
  }, [isPrinting, effectiveFilter, animationControl.isCropMode, animationControl.cropRange, elapsedSeconds]);

  const {data: corporation = null, isLoading: isCorporationLoading} = useQuery({
    queryFn: corporationService.entity,
    queryKey: garrisonTimelineKeys.corporation()
  })

 return(
   <Template isLoading={isCorporationLoading || isLoadingRoute}>
     <Template.Header>
       <div className="flex justify-between w-full">
         <Template.Title>Linha do tempo da guarnição - Rotas</Template.Title>
         <HeaderButtons
           printable={true}
           headerChange={true}
           printComponentRef={printComponentRef}
           setIsPrinting={setIsPrinting}
           reportData={routeData}
           data={garrisonsList?.length}
           headerSelected={headerSelected}
           setHeaderSelected={setHeaderSelected}
         />
       </div>
     </Template.Header>
     <Template.Body className="space-y-2">
       <PrintTemplate printHeaderClassname="hidden print:block"
                      printFooterClassname="hidden print:block"
                      ref={printComponentRef}
                      setIsPrinting={setIsPrinting}
                      title="Linha do tempo da guarnição - Rotas"
                      entity={headerSelected}
                      landscape={true}
                      printClassName="text-[#676a6c]! print:text-black">
         <div id="filters-print" className="transform !-translate-y-[8.5px] text-sm" />
         <div className="print:mb-6 print:w-full print:border-b print:border-gray-200" />
         <Card>
           <Card.Body className="text-[18px]! text-[#676a6c]! print:text-black flex items-center justify-between">
             <div>Guarnição: <span className="font-bold">{firstGarrison?.name || id}</span></div>
             <LegendDropdown items={ROUTE_MAP_LEGEND_ITEMS}/>
           </Card.Body>
         </Card>
         <Card className="relative mt-2">
           <Card.Body className="h-[calc(100vh-238px)] print:h-[650px] print:w-full">
            <GarrisonRouteMap
              corporation={corporation}
              routeData={timelineRoutes}
              garrisonsList={garrisonsList}
              elapsedSeconds={elapsedSeconds}
              filter={effectiveFilter}
              isCropMode={animationControl.isCropMode}
              cropRange={animationControl.cropRange}
            />
           </Card.Body>
         </Card>
       </PrintTemplate>
       <div className="h-[100px] flex gap-2 print:hidden">
         <Card className="h-full flex-1 min-w-[260px] min-h-0 relative z-[60] bg-white">
           <Card.Body className="h-full w-full">
             <GarrisonTimelineControlPanel
              mode={garrisonTimelineModes.route}
              isPlaying={animationControl.isPlaying}
              onTogglePlay={animationControl.togglePlay}
              speedIndex={animationControl.speedIndex}
              setSpeedIndex={animationControl.setSpeedIndex}
              stepSeconds={animationControl.stepSeconds}
              setStepSeconds={animationControl.setStepSeconds}
              onStepForward={animationControl.stepForward}
              onStepBackward={animationControl.stepBackward}
              onRestart={animationControl.restart}
              isCropMode={animationControl.isCropMode}
              onToggleCrop={animationControl.toggleCropMode}
            />
           </Card.Body>
         </Card>
         <Card className="h-full flex-5">
           <GarrisonTimelineSlider
             filter={effectiveFilter}
             zoom={routeZoom}
             isTimeline={false}
             showSeconds={true}
             elapsedSeconds={elapsedSeconds}
             setElapsedSeconds={setElapsedSeconds}
            isCropMode={animationControl.isCropMode}
            cropRange={animationControl.cropRange}
            setCropRange={animationControl.setCropRange}
           />
         </Card>
       </div>
     </Template.Body>
   </Template>
 )
};

export default GarrisonRouteView;
